export const ADS_LEAD_ATTRIBUTION_STORAGE_KEY = 'ads_lead_attribution';

export interface AdsLeadTouchpointAttribution {
  fbclid: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  captured_at: string;
  initiate_checkout_fired?: boolean;
  custom_registration_fired?: boolean;
  subscribe_fired?: boolean;
}

export interface AdsLeadAttribution {
  first_touch_point: AdsLeadTouchpointAttribution | null;
  last_touch_point: AdsLeadTouchpointAttribution | null;
}

export type EventFiredFlag =
  | 'initiate_checkout_fired'
  | 'custom_registration_fired'
  | 'subscribe_fired';

/**
 * Safely reads ads_lead_attribution from localStorage.
 */
export function getAdsLeadAttribution(): AdsLeadAttribution | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(ADS_LEAD_ATTRIBUTION_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as AdsLeadAttribution;
  } catch (error) {
    console.error('Failed to parse ads_lead_attribution from localStorage:', error);
    return null;
  }
}

/**
 * Safely writes ads_lead_attribution to localStorage.
 */
export function saveAdsLeadAttribution(attribution: AdsLeadAttribution): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(ADS_LEAD_ATTRIBUTION_STORAGE_KEY, JSON.stringify(attribution));
  } catch (error) {
    console.error('Failed to save ads_lead_attribution to localStorage:', error);
  }
}

/**
 * Marks a specific event boolean flag = true on last_touch_point in localStorage.
 */
export function markEventFired(eventFiredFlag: EventFiredFlag): void {
  const attribution = getAdsLeadAttribution();
  if (!attribution || !attribution.last_touch_point) return;

  attribution.last_touch_point[eventFiredFlag] = true;
  saveAdsLeadAttribution(attribution);
}

/**
 * Generic tracker for Meta Pixel events that dispatches fbq calls and sets attribution localStorage flags for ad leads.
 */
export interface TrackMetaEventOptions {
  isCustom?: boolean;
  eventFiredFlag?: EventFiredFlag;
  eventData?: Record<string, any>;
}

export function trackMetaEvent(eventName: string, options?: TrackMetaEventOptions): void {
  try {
    const { isCustom = false, eventFiredFlag, eventData } = options || {};

    const attribution = getAdsLeadAttribution();
    const isLeadFromAds = Boolean(
      attribution?.last_touch_point?.fbclid || attribution?.first_touch_point?.fbclid
    );

    // Skip tracking if visitor is not an ad lead or if event flag already fired
    if (!isLeadFromAds || (eventFiredFlag && attribution?.last_touch_point?.[eventFiredFlag] === true)) {
      return;
    }

    if (typeof window !== 'undefined' && typeof (window as any).fbq === 'function') {
      if (eventData) {
        (window as any).fbq(isCustom ? 'trackCustom' : 'track', eventName, eventData);
      } else {
        (window as any).fbq(isCustom ? 'trackCustom' : 'track', eventName);
      }
    }

    if (eventFiredFlag) {
      markEventFired(eventFiredFlag);
    }
  } catch (error) {
    console.error(`Error tracking Meta event ${eventName}:`, error);
  }
}


// [OLD IMPLEMENTATION — replaced by trackCustomRegistrationEvent]
// Meta was auto-firing this standard event via pixel, causing duplicate conversions.
// export function trackCompleteRegistrationEvent(): void {
//   trackMetaEvent('CompleteRegistration', {
//     isCustom: false,
//     eventFiredFlag: 'complete_registration_fired',
//   });
// }

/**
 * Fires a standalone Meta Custom Event 'CustomRegistration' on successful signup.
 * Completely independent of the standard CompleteRegistration event.
 * Uses fbq('trackCustom', 'CustomRegistration', { content_name, status }).
 */
export function trackCustomRegistrationEvent(): void {
  trackMetaEvent('CustomRegistration', {
    isCustom: true,
    eventFiredFlag: 'custom_registration_fired',
  });
}

/**
 * Fired when a user initiates plan checkout (Meta Standard Event InitiateCheckout).
 */
export function trackInitiateCheckoutEvent(): void {
  trackMetaEvent('InitiateCheckout', {
    isCustom: false,
    eventFiredFlag: 'initiate_checkout_fired',
  });
}

/**
 * Fired when a user completes payment (Meta Standard Event Subscribe).
 */
export function trackSubscribeEvent(): void {
  trackMetaEvent('Subscribe', {
    isCustom: false,
    eventFiredFlag: 'subscribe_fired',
  });
}

/**
 * Parses query parameters from a search string.
 * Returns an AdsLeadTouchpointAttribution object if fbclid is present.
 */
export function parseAdsLeadUrlAttribution(searchStr: string): AdsLeadTouchpointAttribution | null {
  if (!searchStr) return null;
  try {
    const params = new URLSearchParams(searchStr);
    const fbclid = params.get('fbclid');

    if (!fbclid || !fbclid.trim()) {
      return null;
    }

    const touchpoint: AdsLeadTouchpointAttribution = {
      fbclid: fbclid.trim(),
      captured_at: new Date().toISOString(),
    };

    const utmSource = params.get('utm_source');
    const utmMedium = params.get('utm_medium');
    const utmCampaign = params.get('utm_campaign');
    const utmContent = params.get('utm_content');
    const utmTerm = params.get('utm_term');

    if (utmSource) touchpoint.utm_source = utmSource.trim();
    if (utmMedium) touchpoint.utm_medium = utmMedium.trim();
    if (utmCampaign) touchpoint.utm_campaign = utmCampaign.trim();
    if (utmContent) touchpoint.utm_content = utmContent.trim();
    if (utmTerm) touchpoint.utm_term = utmTerm.trim();

    return touchpoint;
  } catch (error) {
    console.error('Failed to parse URL attribution:', error);
    return null;
  }
}

/**
 * Inspects current URL search params and updates ads_lead_attribution in localStorage.
 * If fbclid exists:
 * - Sets first_touch_point if not set.
 * - Updates last_touch_point always.
 * If fbclid does NOT exist:
 * - Leaves existing storage untouched.
 */
export function processAdsLeadAttributionOnVisit(searchStr: string): void {
  const currentTouchpoint = parseAdsLeadUrlAttribution(searchStr);
  if (!currentTouchpoint) return;

  const existing = getAdsLeadAttribution();

  // If same fbclid is already active on last_touch_point, skip update to preserve existing states.
  if (existing?.last_touch_point?.fbclid === currentTouchpoint.fbclid) {
    return;
  }

  const updated: AdsLeadAttribution = {
    first_touch_point: existing?.first_touch_point || currentTouchpoint,
    last_touch_point: currentTouchpoint,
  };

  saveAdsLeadAttribution(updated);
}
